import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

/** App-level safety net: a crashing surface shows a recoverable panel, not a white screen. */
export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface the component stack so the failing component is identifiable.
    // eslint-disable-next-line no-console
    console.error("[PacificOne] surface error:", error, info.componentStack);
    this.setState({ info });
  }

  render() {
    if (this.state.error) {
      const stack = this.state.info?.componentStack || "";
      const firstComponents = stack.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 6).join("\n");
      return (
        <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center">
          <AlertTriangle size={32} className="text-accent" />
          <h2 className="text-lg font-semibold">This view hit an error</h2>
          <p className="max-w-md text-sm text-slatey">
            {this.state.error.message || "An unexpected error occurred while rendering this surface."}
          </p>
          {firstComponents && (
            <pre className="mono max-w-lg overflow-auto rounded-lg border border-white/10 bg-black/30 p-3 text-left text-[10px] text-slate-500">
              {firstComponents}
            </pre>
          )}
          <button
            onClick={() => this.setState({ error: null, info: null })}
            className="rounded-lg border border-aurora-cyan/40 bg-aurora-cyan/10 px-4 py-1.5 text-sm text-aurora-cyan hover:bg-aurora-cyan/20"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
